import Mention from "@tiptap/extension-mention";
import { ReactNodeViewRenderer, mergeAttributes } from "@tiptap/react";
import posthog from "posthog-js";

import { buildSuggestionMentionContent } from "@/components/tiptap/insert-suggestion";
import { punctuationAfterMention } from "@/components/tiptap/mention/punctuation-after-mention";
import { suggestion } from "@/components/tiptap/suggestion";

import type { SuggestionItem } from "@/types/suggestions";

import { EmailMentionComponent } from "./email-mention-view";
import { resolveMention } from "./resolve-mention";

type InlineContent = { type: "text"; text: string } | { type: "hardBreak" };
type ParagraphNode = {
  type: "emailParagraph";
  content?: InlineContent[];
};

/**
 * Converts a plain-text string into Tiptap JSON nodes so that newlines
 * are represented structurally rather than as literal \n characters
 * (which HTML would collapse to spaces).
 *
 * - Double newline (\n\n) → paragraph break (new emailParagraph)
 * - Single newline (\n) → hardBreak within the same paragraph
 */
function textToTiptapContent(text: string): ParagraphNode[] {
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs.map((para) => {
    const trimmed = para.replace(/^\n+|\n+$/g, "");
    if (!trimmed) return { type: "emailParagraph" } as ParagraphNode;

    const lines = trimmed.split("\n");
    const content: InlineContent[] = [];
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) content.push({ type: "hardBreak" });
      if (lines[i]) content.push({ type: "text", text: lines[i] });
    }
    return { type: "emailParagraph", content } as ParagraphNode;
  });
}

/** Backend sentinel returned when a variable has no context to preview from. */
const PREVIEW_PLACEHOLDER = "Preview Not Available";

/**
 * Builds the content a picked email variable inserts.
 *
 * `resolveOnInsert` is the composer's answer to "is this email sent once, to a
 * recipient I already know?". One-off composers (a chat email thread, a
 * patient compose drawer, sending forms, a patient's treatment plan) pass
 * `true`, so plain-text variables land as editable text the sender can read
 * and correct. Reusable composers (templates, sequences, blasts, signatures)
 * pass `false` and keep the mention, so the token survives into storage and
 * resolves per recipient at send time.
 *
 * Structured variables (images, links) resolve either way — a mention pill
 * can't represent them, and the flag is about text the author edits, not about
 * whether an image renders.
 */
export function buildEmailSuggestionInsertContent(
  item: SuggestionItem,
  { resolveOnInsert }: { resolveOnInsert: boolean },
) {
  const hasRealContent = item.content && item.content !== PREVIEW_PLACEHOLDER;
  const resolved = hasRealContent
    ? resolveMention(item.id, item.label, item.content)
    : null;
  const isPlainText = resolved?.type === "text" && !resolved.marks?.length;

  if (resolved && !isPlainText) {
    return [resolved, { type: "text", text: " " }];
  }
  if (resolveOnInsert && hasRealContent) {
    return textToTiptapContent(item.content + " ");
  }
  // Shares the single mention-content definition with the plain editor's `@`
  // path and the "Insert variable" modal.
  return buildSuggestionMentionContent(item);
}

/**
 * Creates a Tiptap Mention extension configured for email template variables.
 *
 * Trigger character: @
 * Each mention stores { id, label } where id is the variable key (e.g., "provider_name")
 * and label is the display name (e.g., "Provider Name").
 *
 * Accepts a ref so the items function always reads the latest variables,
 * even when they load asynchronously after the editor is created.
 *
 * Uses the shared MentionList popup from components/tiptap/suggestion.
 */
export function createEmailMention(
  variablesRef: { current: SuggestionItem[] },
  options?: { resolveOnInsert?: boolean },
) {
  const resolveOnInsert = options?.resolveOnInsert ?? false;
  return Mention.extend({
    name: "mention",

    addNodeView() {
      return ReactNodeViewRenderer(EmailMentionComponent);
    },

    parseHTML() {
      return [
        {
          tag: 'span[data-type="mention"]',
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return [
        "span",
        mergeAttributes(
          { "data-type": "mention", "data-id": HTMLAttributes["data-id"] },
          HTMLAttributes,
        ),
        HTMLAttributes["data-label"] ?? HTMLAttributes["data-id"] ?? "",
      ];
    },

    addProseMirrorPlugins() {
      return [...(this.parent?.() ?? []), punctuationAfterMention(this.name)];
    },
  }).configure({
    HTMLAttributes: {
      class: "email-mention",
    },
    renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
    renderHTML: ({ node }) => [
      "span",
      {
        "data-type": "mention",
        "data-id": node.attrs.id,
        "data-label": node.attrs.label,
      },
      node.attrs.label ?? node.attrs.id,
    ],
    suggestion: {
      char: "@",
      items: ({ query }: { query: string }) => {
        const variables = variablesRef.current;
        const lowerQuery = query.toLowerCase();
        return variables.filter(
          (v) =>
            v.content &&
            (v.label.toLowerCase().includes(lowerQuery) ||
              v.id.toLowerCase().includes(lowerQuery) ||
              v.type.toLowerCase().includes(lowerQuery)),
        );
      },
      command: ({ editor, range, props }) => {
        const item = props as SuggestionItem;
        posthog.capture("suggestion_inserted", {
          suggestion_id: item.id,
          suggestion_type: item.type,
          surface: "email",
        });
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent(
            buildEmailSuggestionInsertContent(item, { resolveOnInsert }),
          )
          .run();
      },
      ...suggestion,
      render: () => suggestion.render({ insertsText: () => resolveOnInsert }),
    },
  });
}
